import { entries, fromPairs, isEqual, isUndefined, mapValues, pickBy, values } from 'lodash-es'

import { ActionableState, useActionableState } from './useActionableState'

export enum SingleValuedFilterKeys {
  ETD_FROM = 'etdFrom',
  ETD_TO = 'etdTo',
  ETA_FROM = 'etaFrom',
  ETA_TO = 'etaTo',
  DELIVERY_FROM = 'deliveryFrom',
  DELIVERY_TO = 'deliveryTo',
  CONSIGNEE = 'consignee',
  SHIPPER = 'shipper',
  POL = 'pol',
  POD = 'pod',
  BOOKMARK = 'bookmarked',
  PICKUP_FROM = 'pickupFrom',
  PICKUP_TO = 'pickupTo',
}
export type SingleValuedFilterState = ActionableState<string>

export enum MultiValuedFilterKeys {
  STATUS = 'status',
  TYPE = 'type',
  TRANSPORT_MODE = 'transportMode',
  CARBON_OFFSET = 'carbonOffset',
  TRADELANE = 'tradelane',
}
type MultiValuedFilterState = ActionableState<string[]>

export type Filters = Record<SingleValuedFilterKeys, SingleValuedFilterState> &
  Record<MultiValuedFilterKeys, MultiValuedFilterState>
export const FilterKeys = { ...SingleValuedFilterKeys, ...MultiValuedFilterKeys }
/**
 * We want to replicate the behavior of enum here, so would like to use the same name for type and values.
 * Unfortunately eslint doesn't allow to do it.
 * Disabling it following this proposal: https://github.com/typescript-eslint/typescript-eslint/issues/2585.
 */
// eslint-disable-next-line
export type FilterKeys = SingleValuedFilterKeys | MultiValuedFilterKeys
export type FilterValues = Record<SingleValuedFilterKeys, string> & Record<MultiValuedFilterKeys, string[]>

export enum ShipmentStatus {
  STATUS_FILTER_IN_PROGRESS = 'inProgress',
  STATUS_FILTER_ACTIONS_REQUIRED = 'actionsRequired',
  STATUS_FILTER_COMPLETED = 'completed',
  STATUS_FILTER_CANCELED = 'canceled',
}

export enum ShipmentTradelane {
  TRADELANE_FILTER_IMPORT = 'EU Imp',
  TRADELANE_FILTER_EXPORT = 'EU Exp',
  TRADELANE_FILTER_CROSS = 'CrossT',
}

type DefaultFilterValues = Partial<Record<SingleValuedFilterKeys, string>> & Record<MultiValuedFilterKeys, string[]>

export const defaultValues: DefaultFilterValues = {
  [FilterKeys.STATUS]: [],
  [FilterKeys.TYPE]: [],
  [FilterKeys.TRANSPORT_MODE]: [],
  [FilterKeys.CARBON_OFFSET]: [],
  [FilterKeys.TRADELANE]: [],
}

type FiltersState = {
  filters: Filters
  resetFilters: () => void
  isDefaultFilters: () => boolean
}

export const useShipmentListFilters = (initialValues?: Partial<DefaultFilterValues>): FiltersState => {
  const filters = fromPairs(
    values(FilterKeys).map((filterKey) => {
      const initialValue = initialValues?.[filterKey]
      const defaultValue = defaultValues[filterKey]
      const filterState = useActionableState(initialValue ?? defaultValue)

      return [filterKey, filterState]
    })
  ) as Filters

  const resetFilters = () => {
    entries(filters).forEach(([filterKey, filter]) => {
      const defaultValue = defaultValues[filterKey]
      filter.setValue(defaultValue)
    })
  }

  const isDefaultFilters = (): boolean => {
    const transformedFilters = mapValues(filters, (filterEntry) => filterEntry.value)
    const activeFilters = pickBy(transformedFilters, (value) => !isUndefined(value))
    return isEqual(activeFilters, defaultValues)
  }

  return {
    filters,
    resetFilters,
    isDefaultFilters,
  }
}
